import { G } from '@mobily/ts-belt';
import { isAxiosError, type AxiosError, type AxiosResponse } from 'axios';
import type { CapitalizedBusinessLine } from '../../../../components/SocialButtons/SocialButtons';
import type { ResponseError } from '../types';
import type { SignUp } from '../../../../types/definitions/vendor/graphql-schema.d';

type EmailPreferences = 'all_emails' | 'opt_out';

// Available signup types: https://github.com/stitchfix/client-service/blob/main/app/models/client_signup_type.rb
export type RegistrationSignupType = Lowercase<SignUp>;

export type RegistrationRequestParameters = {
  user: {
    first_name: string;
    last_name: string;
    email: string;
    shipping_postcode?: string;
    business_line: CapitalizedBusinessLine;
    business_line_inferred?: string;
    region?: string;
    password?: string;
    signup_type?: RegistrationSignupType;
  };
  options?: {
    email_preference?: EmailPreferences;
    fb_oauth_token?: string;
    google_oauth_token?: string;
    apple_credentials?: {
      apple_client_id?: string;
      apple_user_id?: string;
      auth_code?: string;
    };
    promo_name?: string | null;
    referrer_client_id?: string | null;
    signup_page_referrer?: string | null;
    visitor_uuid?: string | null;
    name_params_optional?: never;
  };
};

export type AppleLoginRequestParameters = {
  user_id: string;
  auth_code: string;
  scope: 'default';
};

export type RegistrationRequestParametersOptionalLastName = {
  user: Omit<RegistrationRequestParameters['user'], 'last_name'> & {
    last_name?: string;
  };
  options: Omit<
    RegistrationRequestParameters['options'],
    'name_params_optional'
  > & {
    name_params_optional: true;
  };
};

export type GoogleRegistrationRequestParameters = {
  google_oauth_token: string;
  business_line: CapitalizedBusinessLine;
  shipping_postcode?: string;
  options?: {
    scope?: 'default' | 'mobile' | 'android';
    business_line_inferred?: boolean;
    region?: string;
    signup_type?: RegistrationSignupType;
    email_preference?: EmailPreferences;
    promo_name?: string | null;
    referrer_client_id?: string | null;
    signup_page_referrer?: string | null;
    visitor_uuid?: string | null;
  };
};

interface BaseRegistrationResponse {
  accessToken: string;
  refreshToken: string;
  status: 201;
}

// Reference: [success_response](https://github.com/stitchfix/client-auth-api/blob/main/app/controllers/api/v1/registrations_controller.rb#L149-L158)
type SignUpType = 'Facebook' | 'Google' | 'Apple' | 'Email';
type OnboardingType = 'direct_shop' | 'fix';

export interface RegistrationResponseCreated extends BaseRegistrationResponse {
  signUpType: SignUpType;
  externalId: string;
  kidExternalId: null;
  isShell: boolean;
  onboardingType: OnboardingType;
  loggedInExistingClient?: undefined;
}

export interface RegistrationResponseExisting extends BaseRegistrationResponse {
  loggedInExistingClient: true;
}

export const isRegistrationResponseExisting = (
  registrationResponse: RegistrationResponse,
): registrationResponse is RegistrationResponseExisting =>
  G.isBoolean(registrationResponse?.loggedInExistingClient) &&
  registrationResponse.loggedInExistingClient;

export type RegistrationResponse =
  | RegistrationResponseCreated
  | RegistrationResponseExisting;

// ======
// Errors
// ======
export interface RegistrationError extends AxiosError {
  response: AxiosResponse<ResponseError>;
}

const isRegistrationResponseError = (
  response: AxiosResponse,
): response is RegistrationError['response'] =>
  (response.status === 400 && G.isArray(response.data.errors)) ||
  response.status === 403;

/**
 * Axios doesn't surface an easy way to type errors with generics. This
 * strategy is taken from an [Github issue comment](https://github.com/axios/axios/issues/3612#issuecomment-1294232173).
 */
export const isRegistrationError = (
  error: unknown,
): error is RegistrationError =>
  isAxiosError(error) &&
  G.isObject(error.response) &&
  isRegistrationResponseError(error.response);
